// Helpers for translating between three address spaces:
//   1. logical path  — what the user sees in breadcrumbs: "assets/sub/folder"
//   2. relative path — the part under assets/: "sub/folder"
//   3. S3 key        — full bucket key sent to the server
//
// The same component is mounted in two modes:
//   • scene mode  — captionPathPrefix === ""        → keys live at scenes/org/scene/assets/*
//   • caption mode — captionPathPrefix === "<id>/<v>" → keys live at scenes/org/scene/<id>/<v>/assets/*

export const ASSETS_ROOT = 'assets';

export const normalizeSlashes = (value = '') => String(value).replace(/\/+/g, '/');

export const stripLeadingSlash = (value = '') => String(value).replace(/^\/+/, '');
export const stripTrailingSlash = (value = '') => String(value).replace(/\/+$/, '');
export const trimSlashes = (value = '') => stripLeadingSlash(stripTrailingSlash(value));

/** "assets" → "", "assets/foo/bar" → "foo/bar" */
export const logicalToRelative = (logicalPath = '') => {
  const norm = trimSlashes(normalizeSlashes(logicalPath));
  if (!norm || norm === ASSETS_ROOT) return '';
  if (norm.startsWith(`${ASSETS_ROOT}/`)) return norm.slice(ASSETS_ROOT.length + 1);
  return norm;
};

/** "" → "assets", "foo/bar" → "assets/foo/bar" */
export const relativeToLogical = (relativePath = '') => {
  const rel = trimSlashes(relativePath);
  return rel ? `${ASSETS_ROOT}/${rel}` : ASSETS_ROOT;
};

/**
 * Build the absolute S3 key (no leading slash) for a logical path inside the explorer.
 * Always returns a key without a trailing slash; callers add it for folder semantics.
 */
export const buildS3Key = ({ organizationName, sceneName, captionPathPrefix, logicalPath }) => {
  const org = trimSlashes(organizationName || '');
  const scene = trimSlashes(sceneName || '');
  const caption = trimSlashes(captionPathPrefix || '');
  const relative = logicalToRelative(logicalPath);
  const segments = ['scenes', org, scene];
  if (caption) segments.push(caption);
  segments.push(ASSETS_ROOT);
  if (relative) segments.push(relative);
  return normalizeSlashes(segments.filter(Boolean).join('/'));
};

/** Convert a full S3 key (returned by the list API) back to a logical path. */
export const s3KeyToLogical = ({ organizationName, sceneName, captionPathPrefix, fullKey }) => {
  if (!fullKey) return ASSETS_ROOT;
  const org = trimSlashes(organizationName || '');
  const scene = trimSlashes(sceneName || '');
  const caption = trimSlashes(captionPathPrefix || '');
  const base = caption
    ? `scenes/${org}/${scene}/${caption}/`
    : `scenes/${org}/${scene}/`;
  const trimmed = stripTrailingSlash(fullKey);
  if (!trimmed.startsWith(base)) return ASSETS_ROOT;
  const rest = trimmed.slice(base.length);
  if (!rest) return ASSETS_ROOT;
  if (rest === ASSETS_ROOT) return ASSETS_ROOT;
  if (rest.startsWith(`${ASSETS_ROOT}/`)) return rest;
  return `${ASSETS_ROOT}/${rest}`;
};

/** Split a logical path into ["assets", "sub", "folder"] for breadcrumbs. */
export const logicalToCrumbs = (logicalPath = '') => {
  const norm = trimSlashes(logicalPath) || ASSETS_ROOT;
  const parts = norm.split('/').filter(Boolean);
  if (parts[0] !== ASSETS_ROOT) return [ASSETS_ROOT, ...parts];
  return parts;
};

/** Build the URL splat (e.g. /assets/<here>) from a logical path. */
export const logicalToUrlSegment = (logicalPath = '') => logicalToRelative(logicalPath);

/** Inverse of logicalToUrlSegment — what the route splat maps back to. */
export const urlSegmentToLogical = (segment = '') => relativeToLogical(segment);

export const joinLogical = (currentLogical, name) => {
  const base = stripTrailingSlash(currentLogical || ASSETS_ROOT) || ASSETS_ROOT;
  return normalizeSlashes(`${base}/${stripLeadingSlash(name)}`);
};

export const parentLogical = (logicalPath = ASSETS_ROOT) => {
  const norm = stripTrailingSlash(logicalPath || ASSETS_ROOT);
  const parts = norm.split('/').filter(Boolean);
  if (parts.length <= 1) return ASSETS_ROOT;
  parts.pop();
  return parts.join('/');
};

export const getFileExtension = (filename = '') => {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot <= 0) return '';
  return filename.substring(lastDot);
};

export const formatFileSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const getFileTypeFromName = (filename = '') => {
  const ext = filename.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'avif', 'bmp'].includes(ext)) return 'image';
  if (['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg', 'm4a', 'flac'].includes(ext)) return 'audio';
  if (ext === 'pdf') return 'pdf';
  if (['js', 'mjs', 'ts', 'tsx', 'jsx', 'html', 'css', 'py', 'json', 'sh', 'yml', 'yaml', 'xml'].includes(ext)) return 'code';
  if (['glb', 'gltf', 'obj', 'fbx', 'stl'].includes(ext)) return 'model';
  if (['zip', 'tar', 'gz', '7z', 'rar'].includes(ext)) return 'archive';
  return 'document';
};

export const EDITABLE_EXTENSIONS = new Set([
  'js', 'jsx', 'ts', 'tsx', 'mjs', 'html', 'htm', 'css', 'scss', 'sass',
  'json', 'xml', 'py', 'php', 'java', 'cpp', 'cc', 'cxx', 'c', 'cs', 'go',
  'rs', 'rb', 'sh', 'bash', 'sql', 'md', 'markdown', 'yaml', 'yml', 'toml',
  'ini', 'cfg', 'txt', 'log',
]);

export const isEditableFile = (file) => {
  if (!file || file.type !== 'file') return false;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return EDITABLE_EXTENSIONS.has(ext);
};

export const sanitizeFolderName = (name = '') =>
  String(name).trim().replace(/[\\/]+/g, '_').replace(/\s+/g, ' ');

export const sanitizeFileName = (name = '') =>
  String(name).trim().replace(/[\\/]+/g, '_');
